import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Resizable 3-panel layout: Code | Preview | Chat
 * With draggable dividers and collapsible panels.
 */
export default function SplitLayout({ leftPanel, centerPanel, rightPanel, className = '' }) {
  const containerRef = useRef(null);
  const [leftWidth, setLeftWidth] = useState(40); // % of total
  const [rightWidth, setRightWidth] = useState(25); // % of total
  const [dragging, setDragging] = useState(null); // 'left' | 'right' | null

  const handleMouseDown = useCallback((divider) => (e) => {
    e.preventDefault();
    setDragging(divider);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;

    if (dragging === 'left') {
      const newLeft = Math.max(15, Math.min(60, x));
      setLeftWidth(newLeft);
    } else if (dragging === 'right') {
      const newRight = Math.max(15, Math.min(50, 100 - x));
      setRightWidth(newRight);
    }
  }, [dragging]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const centerWidth = 100 - leftWidth - rightWidth;

  return (
    <div
      ref={containerRef}
      className={`flex h-full overflow-hidden rounded-lg border border-border ${className}`}
    >
      {/* Left panel */}
      <div style={{ width: `${leftWidth}%` }} className="flex-shrink-0 overflow-hidden">
        {leftPanel}
      </div>

      {/* Left divider */}
      <div
        onMouseDown={handleMouseDown('left')}
        className={`w-1 flex-shrink-0 cursor-col-resize transition-colors ${
          dragging === 'left' ? 'bg-primary' : 'bg-border hover:bg-primary/50'
        }`}
      />

      {/* Center panel */}
      <div style={{ width: `${centerWidth}%` }} className="flex-shrink-0 overflow-hidden">
        {centerPanel}
      </div>

      {/* Right divider */}
      <div
        onMouseDown={handleMouseDown('right')}
        className={`w-1 flex-shrink-0 cursor-col-resize transition-colors ${
          dragging === 'right' ? 'bg-primary' : 'bg-border hover:bg-primary/50'
        }`}
      />

      {/* Right panel */}
      <div style={{ width: `${rightWidth}%` }} className="flex-shrink-0 overflow-hidden">
        {rightPanel}
      </div>
    </div>
  );
}
